import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import DataTable from '../../../components/Table/DataTable.jsx';
import { LIST_INVENTARIOS } from './gql.js';

const columns = [
  { key: 'sucursal', label: 'Sucursal' },
  { key: 'fechaInicio', label: 'Inicio' },
  { key: 'fechaCierre', label: 'Cierre' },
  { key: 'estado', label: 'Estado' },
  { key: 'items', label: 'Ítems' },
];

export default function InventariosList({ onCreate, onSelect, refreshKey = 0 }) {
  const [sucursalId, setSucursalId] = useState('');
  const [estado, setEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const variables = useMemo(() => {
    const filters = {};
    if (sucursalId.trim()) {
      filters.sucursalId = sucursalId.trim();
    }
    if (estado) {
      filters.estado = estado;
    }
    if (fechaDesde || fechaHasta) {
      filters.fecha = {
        from: fechaDesde || null,
        to: fechaHasta || null,
      };
    }
    return {
      filters,
      pagination: { limit: 50, offset: 0 },
    };
  }, [estado, fechaDesde, fechaHasta, sucursalId]);

  const [{ data, fetching, error }, reexecute] = useQuery({ query: LIST_INVENTARIOS, variables });

  useEffect(() => {
    reexecute({ requestPolicy: 'network-only' });
  }, [reexecute, refreshKey]);

  const rows = useMemo(() => {
    if (!data?.listInventarios?.items?.length) {
      return [];
    }
    return data.listInventarios.items.map((inv) => ({
      id: inv.id,
      sucursal: inv.sucursalNombre || `#${inv.sucursalId}`,
      fechaInicio: inv.fechaInicio ? new Date(inv.fechaInicio).toLocaleString() : '—',
      fechaCierre: inv.fechaCierre ? new Date(inv.fechaCierre).toLocaleString() : '—',
      estado: inv.estado,
      items: inv.items?.length ?? 0,
    }));
  }, [data]);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Inventarios físicos"
        subtitle="Controlá los inventarios por sucursal y generá ajustes automáticos."
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Abrir inventario
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="inventarios-sucursal">
              Sucursal
            </label>
            <input
              id="inventarios-sucursal"
              value={sucursalId}
              onChange={(event) => setSucursalId(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              placeholder="ID de sucursal"
            />
          </div>
          <div className="col-span-12 sm:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="inventarios-estado">
              Estado
            </label>
            <select
              id="inventarios-estado"
              value={estado}
              onChange={(event) => setEstado(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Todos</option>
              <option value="ABIERTO">Abiertos</option>
              <option value="CERRADO">Cerrados</option>
            </select>
          </div>
          <div className="col-span-12 sm:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="inventarios-desde">
              Desde
            </label>
            <input
              type="date"
              id="inventarios-desde"
              value={fechaDesde}
              onChange={(event) => setFechaDesde(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 sm:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="inventarios-hasta">
              Hasta
            </label>
            <input
              type="date"
              id="inventarios-hasta"
              value={fechaHasta}
              onChange={(event) => setFechaHasta(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
          {fetching ? (
            <div className="px-4 py-6 text-sm text-muted">Cargando inventarios…</div>
          ) : error ? (
            <div className="px-4 py-6 text-sm text-red-300">{error.message}</div>
          ) : (
            <DataTable columns={columns} rows={rows} onRowClick={(row) => onSelect?.(row.id)} pageSize={10} />
          )}
        </div>
      </section>

      <StickyActions
        left={<p className="text-sm text-muted">Recordá cerrar el inventario para generar ajustes automáticos en Kardex.</p>}
        right={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Nuevo inventario
          </button>
        }
      />
    </div>
  );
}
